import React, { useCallback, useMemo, useRef, useState } from "react";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../DataTable";
import { PageChanger } from "../PageChanger";
import { SearchBar } from "../SearchBar";
import { timeAgo } from "@/utils/generic";
import { useSpotUser } from "@/utils/spotUser";
import {
  KnowledgeBaseQaSettingsInput,
  KnowledgeBasesV2Service,
  KnowledgeBaseV2,
} from "@/generated";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { TruncatedTextWithTooltip } from "../TruncatedTextWithTooltip";
import { NoxusIcon } from "../UI/NoxusIcon";
import { NoxusEmptyBlock } from "../UI/NoxusEmptyBlock";
import BaseDialog from "../Modals/BaseDialog";
import { SearchX } from "lucide-react";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { ToolConfigStep } from "./ToolConfigStep";
import { IngestedBadge } from "../KnowledgeBases/IngestedBadge";

const PAGE_SIZE = 8;

const columnHelper = createColumnHelper<KnowledgeBaseV2>();

const columns: ColumnDef<KnowledgeBaseV2, any>[] = [
  columnHelper.accessor("name", {
    cell: (info) => {
      return (
        <div className="flex gap-2 items-center">
          <NoxusIcon icon={(info.row.original.meta?.icon as string) || "kb"} />
          <TruncatedTextWithTooltip>{info.getValue()}</TruncatedTextWithTooltip>
        </div>
      );
    },
    header: "Name",
    meta: {
      width: "50%",
    },
  }),
  columnHelper.accessor("created_at", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue() ? timeAgo(info.getValue()) : "-"}
      </p>
    ),
    header: "Created date",
    meta: {
      width: "30%",
    },
  }),
  columnHelper.display({
    id: "documents",
    cell: ({ row }) => {
      const total = row.original.num_docs ?? 0;
      if (total === 0) {
        return <span className="text-sm text-muted-foreground">-</span>;
      }
      return (
        <span className="text-sm tabular-nums">{total.toLocaleString()}</span>
      );
    },
    header: "Documents",
    meta: {
      width: "10%",
    },
  }),
  columnHelper.display({
    id: "ingested",
    cell: ({ row }) => {
      const total = row.original.num_docs ?? 0;
      if (total === 0) {
        return <span className="text-sm text-muted-foreground">-</span>;
      }
      return (
        <IngestedBadge
          ingested={row.original.trained_documents ?? 0}
          total={total}
        />
      );
    },
    header: "Ingested",
    meta: {
      width: "10%",
    },
  }),
];

interface KBPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectKnowledgeBase: (props: {
    knowledgeBaseId: string;
    config: Record<string, unknown>;
  }) => void;
  filteredKnowledgeBases?: string[];
  editingTool?: KnowledgeBaseQaSettingsInput;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
}

export const KnowledgeBasePickerModal = ({
  isOpen,
  onClose,
  onSelectKnowledgeBase,
  filteredKnowledgeBases,
  editingTool,
  itemConfig,
}: KBPickerProps) => {
  const { spotGroup } = useSpotUser();

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState<
    string | null
  >(editingTool?.kb_id || null);

  const toolConfigRef = useRef<Record<string, unknown>>({
    extra_instructions: editingTool?.extra_instructions ?? "",
    tool_display_message_start:
      editingTool?.tool_display_message_start ?? "Searching knowledge base...",
    tool_display_message_end:
      editingTool?.tool_display_message_end ?? "Knowledge base search complete",
    advanced_search: editingTool?.advanced_search ?? true,
    clamp_top_k: editingTool?.clamp_top_k ?? false,
    top_k_window: editingTool?.top_k_window ?? [1, 100],
    private: editingTool?.private ?? false,
    enable_browse_tools: editingTool?.enable_browse_tools ?? true,
    include_tree_in_instructions:
      editingTool?.include_tree_in_instructions ?? false,
    tree_depth_in_instructions: editingTool?.tree_depth_in_instructions ?? 3,
    link_references_to_noxus_file:
      editingTool?.link_references_to_noxus_file ?? false,
  });

  const handleToolConfigChange = useCallback(
    (values: Record<string, unknown>) => {
      toolConfigRef.current = values;
    },
    []
  );

  const { data: knowledgeBaseData, isLoading: isLoadingKbs } = useQuery({
    queryKey: [DATA_KEYS.WORKFLOWS, currentPage, search],
    queryFn: () =>
      KnowledgeBasesV2Service.getKnowledgeBasesV2({
        path: { group_id: spotGroup.id },
        query: {
          size: PAGE_SIZE,
          order_by: "created_at",
          sort: "desc",
          query: search || undefined,
          page: currentPage,
        },
      }),
  });

  const knowledgeBaseName = useMemo(() => {
    if (!selectedKnowledgeBaseId) return null;
    return knowledgeBaseData?.data.items.find(
      (kb) => kb.id === selectedKnowledgeBaseId
    )?.name;
  }, [selectedKnowledgeBaseId, knowledgeBaseData]);

  const data = useMemo(() => {
    if (filteredKnowledgeBases) {
      return knowledgeBaseData?.data.items.filter(
        (kb) => !filteredKnowledgeBases.includes(kb.id)
      );
    }
    return knowledgeBaseData?.data.items;
  }, [knowledgeBaseData, filteredKnowledgeBases]);

  const handleBack = () => {
    setSelectedKnowledgeBaseId(null);
  };

  const handleSave = () => {
    if (selectedKnowledgeBaseId) {
      onSelectKnowledgeBase({
        knowledgeBaseId: selectedKnowledgeBaseId,
        config: toolConfigRef.current,
      });
      onClose();
    }
  };

  const footerActions = selectedKnowledgeBaseId
    ? [
        ...(editingTool
          ? []
          : [
              {
                label: "Back",
                props: {
                  variant: "outline",
                  onClick: handleBack,
                },
              },
            ]),
        {
          label: editingTool ? "Save" : "Add knowledge base",
          props: {
            onClick: handleSave,
            variant: "default",
          },
        },
      ]
    : [
        {
          label: "Cancel",
          props: {
            variant: "outline",
            onClick: onClose,
          },
        },
      ];

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      size="5xl"
      title={
        knowledgeBaseName ? (
          <div className="flex items-center gap-2">
            <NoxusIcon icon="kb" />
            {knowledgeBaseName}
          </div>
        ) : (
          "Add Knowledge Base"
        )
      }
      footerActions={footerActions}
    >
      <div className="-m-1 overflow-x-hidden p-1">
        <AnimatePresence mode="wait" initial={false}>
          {!selectedKnowledgeBaseId ? (
            <motion.div
              key="step1"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div>
                <div className="mb-4">
                  <SearchBar
                    placeholder="Search knowledge bases"
                    setValue={setSearch}
                    value={search}
                    isDebounced
                    autoFocus
                  />
                </div>
                <div className="w-full">
                  <DataTable
                    isLoading={isLoadingKbs}
                    loadingRows={8}
                    columns={columns}
                    data={data || []}
                    rowOnClick={(id, value) => {
                      setSelectedKnowledgeBaseId(value.id);
                    }}
                    noDataMessage="No more knowledge bases available"
                    emptyState={
                      search ? (
                        <NoxusEmptyBlock
                          title="No knowledge bases"
                          description={
                            <>
                              No knowledge bases match &ldquo;{search}&rdquo;.
                            </>
                          }
                          icon={SearchX}
                          size="sm"
                          button="Clear search"
                          buttonVariant="outline"
                          onClick={() => setSearch("")}
                        />
                      ) : undefined
                    }
                    noShadow
                  />
                  {!!knowledgeBaseData?.data.pages &&
                    knowledgeBaseData?.data.pages > 1 && (
                      <footer className="flex justify-center w-full pt-6">
                        <PageChanger
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          totalPages={knowledgeBaseData?.data.pages || 0}
                          maxWidth="3rem"
                          size="sm"
                        />
                      </footer>
                    )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              {itemConfig && (
                <ToolConfigStep
                  itemConfig={itemConfig}
                  defaultValues={toolConfigRef.current}
                  onChange={handleToolConfigChange}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BaseDialog>
  );
};
